/* Raw Water Co. — CRV cart sync (storefront).
   Keeps a CRV product line in the cart matched 1:1 to the water box line:
     - CRV quantity  = box quantity   (CRV product is priced per case: $1.20 = 1 case)
     - CRV selling plan = box's selling plan (same plan IDs are shared by both products),
       so one-time boxes get one-time CRV, subscription boxes get matching-frequency CRV.
   Shows CRV as a real line item at checkout for BOTH pathways (no cart transform needed).
   Checkout can't change quantities, so cart is the only place sync is required. */
(function () {
  // --- Config: product IDs (NOT variant IDs) as exposed by /cart.js line.product_id ---
  var BOX_PRODUCT_ID = 12436793622846;
  var CRV_PRODUCT_ID = 12455207305534;
  var CRV_VARIANT_ID = 53725344956734; // used when adding the CRV line

  var syncing = false; // guard against reacting to our own cart writes

  function getCart() {
    return fetch("/cart.js", { headers: { "Accept": "application/json" } }).then(function (r) { return r.json(); });
  }

  // Box line's selling_plan id (or null for one-time)
  function boxPlanId(line) {
    return (line.selling_plan_allocation && line.selling_plan_allocation.selling_plan && line.selling_plan_allocation.selling_plan.id) || null;
  }
  function crvPlanId(line) {
    return boxPlanId(line); // same field on the CRV line
  }

  function changeLine(key, quantity, sellingPlan) {
    var body = { id: key, quantity: quantity };
    // /cart/change keeps selling plan if we don't pass it; for plan changes we re-add instead.
    return fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }

  function addCrv(quantity, sellingPlan) {
    var item = { id: CRV_VARIANT_ID, quantity: quantity };
    if (sellingPlan) item.selling_plan = sellingPlan;
    return fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify({ items: [item] })
    }).then(function (r) { return r.json(); });
  }

  function removeByKey(key) {
    return fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify({ id: key, quantity: 0 })
    }).then(function (r) { return r.json(); });
  }

  function sync() {
    if (syncing) return Promise.resolve();
    syncing = true;
    return getCart().then(function (cart) {
      var boxLine = null, crvLine = null;
      cart.items.forEach(function (l) {
        if (l.product_id === BOX_PRODUCT_ID) boxLine = l;
        if (l.product_id === CRV_PRODUCT_ID) crvLine = l;
      });

      // No box in cart -> remove any stray CRV.
      if (!boxLine) {
        if (crvLine) return removeByKey(crvLine.key).then(function () { return true; });
        return false;
      }

      var wantQty = boxLine.quantity;          // CRV qty = box qty (per case)
      var wantPlan = boxPlanId(boxLine);       // null = one-time, else box's plan id

      if (!crvLine) {
        return addCrv(wantQty, wantPlan).then(function () { return true; });
      }

      var havePlan = crvPlanId(crvLine);
      // If the plan changed (one-time<->sub or different frequency), re-create the CRV line.
      if (String(havePlan || "") !== String(wantPlan || "")) {
        return removeByKey(crvLine.key).then(function () { return addCrv(wantQty, wantPlan); }).then(function () { return true; });
      }
      // Plan matches; just fix quantity if needed.
      if (crvLine.quantity !== wantQty) {
        return changeLine(crvLine.key, wantQty, wantPlan).then(function () { return true; });
      }
      return false; // already in sync, nothing changed
    }).then(function (changed) {
      syncing = false;
      return changed;
    }).catch(function () {
      syncing = false;
      return false;
    });
  }

  var onCartPage = window.location.pathname.indexOf("/cart") === 0;

  // Refresh the cart UI after a change. On the cart page we do a full reload:
  // it's bulletproof (the cart always renders correctly from the server) and
  // avoids fighting Dawn's AJAX cart rendering / custom-element re-upgrade, which
  // proved unreliable. The ~200ms flash is an acceptable trade for correctness.
  function rerenderCart() {
    if (onCartPage) {
      window.location.reload();
      return Promise.resolve();
    }
    // Off the cart page (drawer/PDP), let Dawn refresh its own cart sections.
    document.documentElement.dispatchEvent(new CustomEvent("rwc:crv-synced"));
    return Promise.resolve();
  }

  // sync() returns whether it changed the cart; if so, AJAX-refresh the UI.
  function syncAndRefresh() {
    return sync().then(function (changed) { if (changed) return rerenderCart(); });
  }

  // On the cart page, force the BOX line's quantity control to behave in multiples
  // of MIN/STEP (5). Shopify quantity_rule is B2B-only, so we enforce it here:
  //  - set min/step on the box quantity input
  //  - make the +/- buttons jump by STEP
  // After any change, the qty is snapped to a valid multiple and CRV re-syncs.
  var STEP = 5;
  function enforceBoxStepper() {
    if (!onCartPage) return;
    // Find the box line's quantity input. Dawn renders inputs with the variant id;
    // we match by the box product via the line item's data, falling back to the
    // input whose row references the box. Simplest reliable hook: data-quantity-variant.
    var inputs = document.querySelectorAll('.cart-quantity input.quantity__input, quantity-input input[name="quantity"]');
    inputs.forEach(function (input) {
      // Identify the box row: the cart item row contains the box product link/title.
      var row = input.closest("[id^='CartItem-'], tr, .cart-item");
      var isBox = row && /icelandic-glacial-still-natural-spring-mineral-water/i.test(row.innerHTML);
      if (!isBox) return;
      if (input.dataset.rwcStepBound) return;
      input.dataset.rwcStepBound = "1";
      input.setAttribute("step", STEP);
      input.setAttribute("min", STEP);
      input.setAttribute("data-min", STEP);
      input.min = String(STEP);
      input.step = String(STEP);

      var current = parseInt(input.value, 10) || STEP;
      var qInput = input.closest("quantity-input");

      // Fully OWN the box qty controls so Dawn never posts a box-only change
      // (which would mismatch CRV and trip the validator). We compute the new
      // value and update box + CRV atomically ourselves.
      if (qInput) {
        var minus = qInput.querySelector('[name="minus"]');
        var plus = qInput.querySelector('[name="plus"]');
        if (plus) plus.addEventListener("click", function (e) {
          e.preventDefault(); e.stopImmediatePropagation();
          var cur = parseInt(input.value, 10) || STEP;
          atomicBoxCrvUpdate(cur + STEP);
        }, true);
        if (minus) minus.addEventListener("click", function (e) {
          e.preventDefault(); e.stopImmediatePropagation();
          var cur = parseInt(input.value, 10) || STEP;
          atomicBoxCrvUpdate(Math.max(STEP, cur - STEP));
        }, true);
      }

      // Typed value: snap to multiple of 5 and atomic-update. Capture phase +
      // stopImmediatePropagation prevents Dawn's own change->post from firing.
      input.addEventListener("change", function (e) {
        e.preventDefault(); e.stopImmediatePropagation();
        var v = parseInt(input.value, 10);
        if (isNaN(v) || v < STEP) v = STEP;
        v = Math.round(v / STEP) * STEP;
        if (v < STEP) v = STEP;
        atomicBoxCrvUpdate(v);
      }, true);
    });

    bindRemoveButtons();
  }

  // Remove-button rules on the cart page:
  //  - Deleting the BOX clears BOTH box + CRV in one atomic update (no mismatched
  //    in-between state, so no false validation error).
  //  - Deleting the CRV is blocked (it must follow the box).
  function bindRemoveButtons() {
    if (!onCartPage) return;
    document.querySelectorAll("cart-remove-button, .cart-remove-button, a[href*='/cart/change']").forEach(function (btn) {
      if (btn.dataset.rwcRemoveBound) return;
      var row = btn.closest("[id^='CartItem-'], tr, .cart-item");
      if (!row) return;
      var html = row.innerHTML;
      var isBox = /icelandic-glacial-still-natural-spring-mineral-water/i.test(html);
      var isCrv = /CRV|Redemption/i.test(html);
      if (!isBox && !isCrv) return;
      btn.dataset.rwcRemoveBound = "1";

      btn.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopImmediatePropagation();
        if (isCrv && !isBox) {
          // Block removing CRV directly.
          alert("The CA Redemption Value (CRV) is required and is tied to your water order. Remove the water to remove the CRV.");
          return;
        }
        if (isBox) {
          // Remove BOTH box + CRV atomically (set both variants to 0 in one update),
          // so there's never a box-without-CRV state for the validator to flag.
          syncing = true;
          getCart().then(function (cart) {
            var updates = {};
            cart.items.forEach(function (l) {
              if (l.product_id === BOX_PRODUCT_ID || l.product_id === CRV_PRODUCT_ID) {
                updates[l.key] = 0;
              }
            });
            return origFetch("/cart/update.js", {
              method: "POST",
              headers: { "Content-Type": "application/json", "Accept": "application/json" },
              body: JSON.stringify({ updates: updates })
            });
          }).then(function () { syncing = false; rerenderCart(); })
            .catch(function () { syncing = false; });
        }
      }, true);
    });
  }

  // Update a cart line quantity via Dawn's mechanism: set the input + dispatch change
  // so Dawn posts the update; our fetch hook then re-syncs CRV + re-renders.
  function setQty(input, qty) {
    input.value = String(qty);
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }

  // Set BOTH box and CRV to the same quantity in ONE /cart/update call. Using line
  // keys preserves each line's selling plan. Atomic = the cart validation never
  // sees a box!=CRV mismatch, so it won't reject/revert the change.
  function atomicBoxCrvUpdate(qty) {
    syncing = true;
    getCart().then(function (cart) {
      var boxLine = null, crvLine = null;
      cart.items.forEach(function (l) {
        if (l.product_id === BOX_PRODUCT_ID) boxLine = l;
        if (l.product_id === CRV_PRODUCT_ID) crvLine = l;
      });
      var updates = {};
      if (boxLine) updates[boxLine.key] = qty;
      if (crvLine) updates[crvLine.key] = qty;
      return origFetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ updates: updates })
      });
    }).then(function () {
      syncing = false;
      return rerenderCart(); // also runs sync() to add CRV if it was somehow missing
    }).catch(function () {
      syncing = false;
    });
  }

  function init() { sync(); enforceBoxStepper(); } // reconcile + bind cart stepper

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Back-navigation from checkout restores the page from bfcache WITHOUT re-running
  // scripts — pageshow.persisted catches that so the cart reconciles + reflects CRV.
  window.addEventListener("pageshow", function (e) {
    if (e.persisted) { if (onCartPage) syncAndRefresh(); else sync(); }
  });

  // Dawn cart events (add / line update).
  document.addEventListener("cart:updated", function () { sync(); });
  document.addEventListener("cart:refresh", function () { sync(); });

  // Intercept native cart writes (add/change/update). When the change isn't our own,
  // re-sync; on the cart page, refresh the UI afterward so qty edits mirror to CRV.
  var origFetch = window.fetch;
  window.fetch = function () {
    var args = arguments;
    var url = (args[0] && args[0].url) || args[0] || "";
    var p = origFetch.apply(this, args);
    if (typeof url === "string" && /\/cart\/(add|change|update)(\.js)?/.test(url) && !syncing) {
      p.then(function () {
        setTimeout(function () {
          if (onCartPage) { syncAndRefresh(); } else { sync(); }
        }, 80);
      });
    }
    return p;
  };

  // CRITICAL: the custom PDP / homepage use a classic <form> POST to /cart/add,
  // which navigates away before our fetch hook can run — so CRV would never be
  // added. Intercept those product-form submits: add the box AND the CRV via AJAX
  // (CRV qty = box qty, same selling plan), THEN go to the cart. Guarantees the
  // CRV line exists before checkout so the server validation passes.
  function addBoxThenCrv(form) {
    var fd = new FormData(form);
    var qty = parseInt(fd.get("quantity") || "1", 10) || 1;
    var plan = fd.get("selling_plan") || null; // present only when Subscribe chosen
    var boxItem = { id: fd.get("id"), quantity: qty };
    if (plan) boxItem.selling_plan = plan;
    var items = [boxItem, { id: CRV_VARIANT_ID, quantity: qty }];
    if (plan) items[1].selling_plan = plan; // CRV mirrors the box's plan (shared IDs)

    syncing = true; // prevent the fetch hook from double-firing
    return origFetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify({ items: items })
    }).then(function () {
      syncing = false;
      window.location.href = "/cart";
    }).catch(function () {
      syncing = false;
      form.submit(); // fall back to native submit if AJAX fails
    });
  }

  document.addEventListener("submit", function (e) {
    var form = e.target;
    if (!form || form.tagName !== "FORM") return;
    // Match the storefront product add forms (custom PDP + homepage products section).
    var action = (form.getAttribute("action") || "");
    var isProductForm = /\/cart\/add/.test(action) && form.querySelector('[name="id"]');
    if (!isProductForm) return;
    // Only handle the box product (CRV is added alongside). Check the variant.
    e.preventDefault();
    addBoxThenCrv(form);
  }, true);
})();
